import { Component, OnInit, inject, signal } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { InputNumberModule } from 'primeng/inputnumber';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ToolbarModule } from 'primeng/toolbar';
import { ToastModule } from 'primeng/toast';
import {
  Assessment,
  AssessmentType,
  CreateAssessment,
  UpdateAssessment,
} from '../../../models/assessment.model';
import { AssessmentService } from '../../../services/assessment.service';
import { FormErrorComponent } from '../../../../shared/components/form-error/form-error.component';
import { FormServerErrorComponent } from '../../../../shared/components/form-server-error/form-server-error.component';
import { ApiResult } from '../../../../shared/models/api-result.model';
import { Observable } from 'rxjs';
import { FormGroupService } from '../../../../shared/services/form-group.service';
import { SelectOption } from '../../../../shared/models/dropdowm.model';

@Component({
  selector: 'app-assessment-create-or-update',
  templateUrl: './assessment-create-or-update.component.html',
  imports: [
    ReactiveFormsModule,
    BreadcrumbModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    ToggleSwitchModule,
    InputNumberModule,
    ProgressSpinnerModule,
    RouterLink,
    FormErrorComponent,
    FormServerErrorComponent,
    ToolbarModule,
    ToastModule,
  ],
})
export class AssessmentCreateOrUpdateComponent implements OnInit {
  private fb = inject(FormBuilder);
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);
  private messageService = inject(MessageService);
  private assessmentService = inject(AssessmentService);
  private formGroupService = inject(FormGroupService);

  formGroup: FormGroup = this.fb.group({
    id: [null],
    name: [null, [Validators.required, Validators.maxLength(255)]],
    description: [null, [Validators.maxLength(255)]],
    type: [null, [Validators.required]],
    isRequired: [false, [Validators.required]],
    isActive: [true],
    priority: [0, [Validators.required, Validators.max(1000), Validators.min(0)]],
  });

  loading = signal(false);
  assessmentId = signal<string | null>(null);

  assessmentTypeOptions: SelectOption[] =
    this.assessmentService.getAssessmentTypeOptions();

  ngOnInit(): void {
    const id = this.activatedRoute.snapshot.paramMap.get('id');
    if (id) { 
      this.assessmentId.set(id);
      this.loadData(id);
    }
  }

  loadData(id: string): void {
    this.loading.set(true);

    this.assessmentService.getById(id).subscribe({
      next: (response: ApiResult) => {
        this.loading.set(false);

        const model = response.data as Assessment;

        this.formGroup.patchValue({
          id: model.id,
          name: model.name,
          description: model.description,
          type: model.type,
          isRequired: model.isRequired,
          isActive: model.isActive,
          priority: model.priority,
        });
      },
      error: (e) => {
        this.loading.set(false);

        if (e instanceof HttpErrorResponse) {
          this.messageService.add({
            severity: 'error',
            summary: 'Falha',
            detail: 'Ocorreu um erro ao carregar a avaliação',
            life: 3000,
          });
        }
      },
    });
  }

  onSubmit(): void {
    if (!this.formGroup.valid) {
      Object.keys(this.formGroup.controls).forEach((key) => {
        const control = this.formGroup.get(key);
        control?.markAsTouched();
      });
      return;
    }

    const id = this.assessmentId();
    const body = this.formGroup.getRawValue();

    this.loading.set(true);

    let request$: Observable<ApiResult>;

    if (id) {
      request$ = this.assessmentService.update(id, body as UpdateAssessment);
    } else {
      request$ = this.assessmentService.create(body as CreateAssessment);
    }

    request$.subscribe({
      next: () => {
        this.loading.set(false);

        this.messageService.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: 'Avaliação salva com sucesso',
          life: 2000,
        });

        this.router.navigate(['/assessments']);
      },
      error: (e) => {
        this.loading.set(false);

        if (e instanceof HttpErrorResponse) {
          if ([400, 422].includes(e.status)) {
            if (
              (e.error.message &&
                (e.error.errors || e.error.errors.length > 0)) ||
              (!e.error.message &&
                (e.error.errors || e.error.errors.length > 0))
            ) {
              this.formGroupService.addServerErrors(
                e.error.errors,
                this.formGroup
              );
            } else if (
              e.error.message &&
              (!e.error.errors || e.error.errors.length == 0)
            ) {
              this.messageService.add({
                severity: 'warn',
                summary: 'Atenção',
                detail: e.error.message,
                life: 3000,
              });
            }
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Falha',
              detail: 'Ocorreu um erro ao salvar a avaliação',
              life: 3000,
            });
          }
        }
      },
    });
  }

  getAssessmentTypeName(type: AssessmentType | null): string {
    if (type === null || type === undefined) return '';

    const option = this.assessmentTypeOptions.find((x) => x.value === type);
    return option ? option.label : '';
  }
}
